import { useRef, useState, useEffect, useCallback } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const RACES = [
  { key: 'HUMAN', name: 'Human', emoji: '\uD83D\uDC64', archetype: 'Offensive \u00B7 Balanced', pros: ['+10% income', 'Versatile spellcasting'], cons: ['No specialization'] },
  { key: 'ORC', name: 'Orc', emoji: '\u2694\uFE0F', archetype: 'Offensive \u00B7 Military', pros: ['+15% off. military eff.', 'Spreads plague on attack'], cons: ['-20% magic effectiveness'] },
  { key: 'UNDEAD', name: 'Undead', emoji: '\uD83D\uDC80', archetype: 'Offensive \u00B7 Attrition', pros: ['Free prisoners on attack', 'Immune to plague'], cons: ['-25% food production'] },
  { key: 'AVIAN', name: 'Avian', emoji: '\uD83E\uDD85', archetype: 'Offensive \u00B7 Speed', pros: ['Faster attack speed', '+30% spell duration'], cons: ['Weaker defense'] },
  { key: 'DWARF', name: 'Dwarf', emoji: '\uD83D\uDEE1\uFE0F', archetype: 'Defensive \u00B7 Fortification', pros: ['+15% def. military eff.', '+20% building eff.'], cons: ['Slower attack speed'] },
  { key: 'HALFLING', name: 'Halfling', emoji: '\uD83C\uDF40', archetype: 'Defensive \u00B7 Economy', pros: ['+25% food production', 'Stronger thievery'], cons: ['Weaker military units'] },
  { key: 'ELF', name: 'Elf', emoji: '\uD83C\uDFF9', archetype: 'Arcane \u00B7 Magic', pros: ['+20% spell effectiveness', 'Strong ranged units'], cons: ['-15% population growth'] },
  { key: 'FAERY', name: 'Faery', emoji: '\u2728', archetype: 'Arcane \u00B7 Specialist', pros: ['+30% spell effectiveness', 'Magic immune units'], cons: ['Weakest military stats'] },
];

export function RacesSection() {
  const { ref, isVisible } = useScrollReveal();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    return () => el.removeEventListener('scroll', updateScrollState);
  }, [updateScrollState]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction === 'left' ? -240 : 240, behavior: 'smooth' });
  };

  return (
    <section id="races" className="px-6 md:px-12 py-24 max-w-[1200px] mx-auto" ref={ref}>
      <div
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}
      >
        <div
          className="mb-3 uppercase tracking-[0.15em]"
          style={{ fontFamily: "'Cinzel', serif", fontSize: 11, fontWeight: 600, color: '#a08030' }}
        >
          Choose Your Race
        </div>
        <h2
          className="mb-4"
          style={{ fontFamily: "'Cinzel', serif", fontSize: 36, fontWeight: 700, color: '#f0e8dc', lineHeight: 1.2 }}
        >
          Eight Races, Endless Strategies
        </h2>
        <p className="mb-12 max-w-[640px]" style={{ color: '#e0d5c8', fontSize: 18 }}>
          Each race brings unique military units, economic bonuses, and magical abilities. Pair with
          one of eight personalities — Warrior, Sage, Merchant, and more — to define your playstyle.
        </p>

        <div className="relative">
          {/* Scroll affordance: gradient fades */}
          {canScrollLeft && (
            <div
              className="absolute left-0 top-0 bottom-4 w-12 z-10 pointer-events-none"
              style={{ background: 'linear-gradient(to right, #110f0d, transparent)' }}
            />
          )}
          {canScrollRight && (
            <div
              className="absolute right-0 top-0 bottom-4 w-12 z-10 pointer-events-none"
              style={{ background: 'linear-gradient(to left, #110f0d, transparent)' }}
            />
          )}

          {/* Arrow buttons */}
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center rounded-full transition-colors hover:bg-game-750"
              style={{ width: 40, height: 40, background: '#1c1916', border: '1px solid #2e2924', cursor: 'pointer' }}
              aria-label="Scroll left"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#b5a99c" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-1/2 -translate-y-1/2 z-20 flex items-center justify-center rounded-full transition-colors hover:bg-game-750"
              style={{ width: 40, height: 40, background: '#1c1916', border: '1px solid #2e2924', cursor: 'pointer' }}
              aria-label="Scroll right"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#b5a99c" strokeWidth="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          )}

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-4"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#2e2924 transparent' }}
          >
            {RACES.map((race) => (
              <div
                key={race.key}
                className="shrink-0 rounded-lg overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-game-600"
                style={{
                  minWidth: 220,
                  background: '#1c1916',
                  border: '1px solid #24201b',
                }}
              >
                <div
                  className="flex items-center justify-center text-6xl"
                  style={{ height: 140, background: '#0a0908' }}
                >
                  {race.emoji}
                </div>
                <div className="p-4 px-5">
                  <div
                    className="mb-1"
                    style={{ fontFamily: "'Cinzel', serif", fontSize: 16, fontWeight: 600, color: '#f0e8dc' }}
                  >
                    {race.name}
                  </div>
                  <div
                    className="uppercase tracking-wider mb-3"
                    style={{ fontSize: 11, color: '#635850' }}
                  >
                    {race.archetype}
                  </div>
                  <div className="flex flex-col gap-1">
                    {race.pros.map((trait) => (
                      <div key={trait} className="flex items-center gap-1.5" style={{ fontSize: 13, color: '#5dcc5d' }}>
                        <span className="shrink-0 rounded-full" style={{ width: 5, height: 5, background: '#5dcc5d' }} />
                        {trait}
                      </div>
                    ))}
                    {race.cons.map((trait) => (
                      <div key={trait} className="flex items-center gap-1.5" style={{ fontSize: 13, color: '#ff5555' }}>
                        <span className="shrink-0 rounded-full" style={{ width: 5, height: 5, background: '#ff5555' }} />
                        {trait}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
